import React, { useEffect, useState } from "react";
import InstantSettingsDialog from "../../components/InstantSettingsDialog";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Button,
  IconButton,
  Drawer,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
} from "@mui/material";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import BoltIcon from "@mui/icons-material/Bolt";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import StorefrontIcon from "@mui/icons-material/Storefront";
import MapIcon from "@mui/icons-material/Map";
import BikeScooterIcon from "@mui/icons-material/BikeScooter";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import StatsCard from "../../components/dashboard/StatsCard";
import dashboardApi from "../../api/dashboard";
import Layout from ".././../components/layout/Layout";
import globalSettingsApi from "../../api/globalSettings";
import notificationsApi from "../../api/notifications";
import useAuth from "../../hooks/useAuth";
import CustomNotificationDialog from "./CustomNotificationDialog";
import ConfirmationDialog from "./ConfirmationDialog";
interface OverviewStats {
  totalUsers: number;
  totalSubscriptions: number;
  activeSubscriptions: number;
  upcomingOrders: number;
  totalRevenue: number;
  increaseInSubscriptionsPercentage: number;
  next10PendingOrders: any[];
  activeSubscriptionsEndingInNext7Days: any[];
  completedSubscriptions: number;
  totalRevenueLastMonth: number;
  totalDeliveredOrders: number;
  supportFlags: Array<{
    _id: string;
    cancellations: number;
    user?: { name?: string; email?: string; phoneNumber?: string };
  }>;
  upcomingMorningDeliveries: number;
  subscriptionRevenueMTD: number;
  activeLiveOrders: number;
  instantOrdersToday: number;
  instantRevenueToday: number;
  activeKitchensStr: string;
  totalSubscriptionRevenue: number;
  totalInstantRevenue: number;
  newSubscriptionsPast7Days: number;
  avgInstantOrdersPast7Days: number;
  liveInstantOrders: Array<{
    _id: string;
    orderNumber: string;
    itemSummary: string;
    status: string;
    isDelayed: boolean;
    elapsedMins: number;
  }>;
  alerts: {
    delayedOrders: Array<{
      _id: string;
      orderNumber: string;
      customerName: string;
      kitchenName: string;
      eta: string;
      delayMins: number;
    }>;
    unassignedOrders: Array<{
      _id: string;
      orderNumber: string;
      customerName: string;
      kitchenName: string;
      elapsedMins: number;
    }>;
    lowInventoryStock: Array<{
      _id: string;
      itemName: string;
      kitchenName: string;
      quantity: number;
      unit: string;
    }>;
  };
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rainNotice, setRainNotice] = useState<boolean>(false);
  const [instantOrderConfig, setInstantOrderConfig] = useState<any>({
    instantEnabled: false,
    freeDeliveryThreshold: 300,
    defaultDeliveryFee: 30,
    orderAcceptTimeoutMins: 5,
    defaultPrepTimeMins: 10,
    maxInstantDiscountPct: 50,
    stockResetTime: "06:45",
    adminChimeEnabled: true,
    newOrderAlertThresholdMins: 3,
    isAutoAssignEnabled: false,
    offlineTitle: "",
    offlineBody: "",
    testerPhones: "",
  });
  const [isInstantSettingsOpen, setIsInstantSettingsOpen] = useState<boolean>(false);
  const [openCustomNotificationDialog, setOpenCustomNotificationDialog] = useState<boolean>(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState<boolean>(false);
  const [pendingOrdersExpanded, setPendingOrdersExpanded] = useState<boolean>(false);
  const [endingSubscriptionsExpanded, setEndingSubscriptionsExpanded] = useState<boolean>(false);
  const [supportFlagsExpanded, setSupportFlagsExpanded] = useState<boolean>(false);
  const [alertsDrawerOpen, setAlertsDrawerOpen] = useState<boolean>(false);

  const { selectedKitchenId } = useAuth();

  const fetchStats = async (showLoadingIndicator = false) => {
    if (showLoadingIndicator) {
      setLoading(true);
    }
    try {
      const data = await dashboardApi.getOverviewStats(selectedKitchenId || undefined);
      setStats(data);
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Error fetching dashboard data"
      );
    } finally {
      if (showLoadingIndicator) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    void fetchStats(true);
    void fetchGlobalSettings();

    const intervalId = setInterval(() => {
      void fetchStats(false);
    }, 20000);

    return () => clearInterval(intervalId);
  }, [selectedKitchenId]);

  const fetchGlobalSettings = async () => {
    const data = await globalSettingsApi.getGlobalSettings();
    setRainNotice(data.globalSettings.rainNotice);
    if (data.globalSettings.instantOrderConfig) {
      const config = data.globalSettings.instantOrderConfig;
      const referrals = data.globalSettings.featureFlags?.referrals || {};
      setInstantOrderConfig({
        ...config,
        walletEnabled: config.walletEnabled ?? (referrals.walletEnabled !== false),
        walletUsageLimitPercent: config.walletUsageLimitPercent ?? referrals.walletUsageLimitPercent ?? 20,
        walletUsageLimitPerOrder: config.walletUsageLimitPerOrder ?? referrals.walletUsageLimitPerSubscription ?? 250,
        combinedDiscountLimitPercent: config.combinedDiscountLimitPercent ?? 50,
        combinedDiscountLimitPerOrder: config.combinedDiscountLimitPerOrder ?? referrals.walletUsageLimitPerSubscription ?? 250,
      });
    }
  };

  const handleRainNoticeChange = async () => {
    await globalSettingsApi.updateGlobalSettings({ rainNotice: !rainNotice });
    setRainNotice(!rainNotice);
  };

  const handleSaveInstantSettings = async (updatedConfig: any) => {
    try {
      await globalSettingsApi.updateGlobalSettings({ instantOrderConfig: updatedConfig });
      setInstantOrderConfig(updatedConfig);
      setIsInstantSettingsOpen(false);
      Swal.fire({
        icon: "success",
        title: "Settings Saved",
        text: "Instant order configuration updated successfully",
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Failed to update configuration"
      });
    }
  };

  const handleMorningNotifications = async () => {
    setOpenConfirmationDialog(true);
  
    
  };
  

  const handleCustomNotification = async () => {
    setOpenCustomNotificationDialog(true);
  };

  const renderDashboardContent = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="70vh"
        >
          <CircularProgress sx={{ color: "#0f172a" }} />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mt: 2, mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!stats) {
      return (
        <Alert severity="info" sx={{ mt: 2, mb: 2, borderRadius: 2 }}>
          No dashboard data available
        </Alert>
      );
    }

    const totalAlertCount = 
      (stats.alerts?.delayedOrders?.length || 0) +
      (stats.alerts?.unassignedOrders?.length || 0) +
      (stats.alerts?.lowInventoryStock?.length || 0);

    return (
      <>
        <style>{`
          @keyframes pulse-live {
            0% { transform: scale(0.95); opacity: 0.5; }
            50% { transform: scale(1.2); opacity: 1; }
            100% { transform: scale(0.95); opacity: 0.5; }
          }
        `}</style>

        {/* Dashboard Header with Alert Action */}
        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", mb: 4, gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="600" color="#0f172a" sx={{ fontSize: "1.875rem", lineHeight: 1.25, letterSpacing: "-0.02em" }}>
              Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Real-time business performance and live delivery operations console
            </Typography>
          </Box>
          {totalAlertCount > 0 ? (
            <Button
              variant="contained"
              onClick={() => setAlertsDrawerOpen(true)}
              startIcon={<WarningAmberIcon />}
              sx={{
                backgroundColor: "#ef4444",
                color: "white",
                borderRadius: "20px",
                fontWeight: 700,
                textTransform: "none",
                fontSize: "0.875rem",
                px: 2.5,
                py: 1,
                boxShadow: "0 4px 10px rgba(239, 68, 68, 0.25)",
                "&:hover": {
                  backgroundColor: "#dc2626",
                }
              }}
            >
              {totalAlertCount} Active Operational Alert{totalAlertCount > 1 ? "s" : ""}
            </Button>
          ) : (
            <Chip 
              label="All systems nominal" 
              color="success" 
              variant="outlined" 
              size="medium"
              sx={{ fontWeight: 600, px: 1.5, color: "#10b981", borderColor: "#a7f3d0", backgroundColor: "#f0fdf4" }}
            />
          )}
        </Box>

        {/* Executive Business Overview */}
        <Box 
          sx={{ 
            display: "grid", 
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, 
            backgroundColor: "white", 
            border: "1px solid #e2e8f0", 
            borderRadius: 4, 
            overflow: "hidden", 
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            mb: 4 
          }}
        >
          {/* Cell 1: Total Users */}
          <Box 
            component={Link}
            to="/users"
            sx={{ 
              p: 3, 
              position: "relative", 
              overflow: "hidden", 
              borderRight: { lg: "1px solid #e2e8f0" }, 
              borderBottom: { xs: "1px solid #e2e8f0", sm: "1px solid #e2e8f0", lg: "none" },
              textDecoration: "none",
              cursor: "pointer",
              transition: "background-color 0.2s",
              "&:hover": { backgroundColor: "#f8fafc" }
            }}
          >
            <Box sx={{ position: "relative", zIndex: 2 }}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
                Total Users
              </Typography>
              <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mt: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
                  {stats.totalUsers.toLocaleString()}
                </Typography>
                <svg width="48" height="18" viewBox="0 0 48 18" fill="none">
                  <path d="M0 12C8 12 12 4 20 6C28 8 32 16 48 2" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Box>
            </Box>
            <PersonOutlineIcon sx={{ position: "absolute", bottom: -12, right: -12, fontSize: 80, color: "#0f172a", opacity: 0.05, pointerEvents: "none", zIndex: 1 }} />
          </Box>

          {/* Cell 2: Total Delivered Orders */}
          <Box 
            component={Link}
            to="/orders"
            sx={{ 
              p: 3, 
              position: "relative", 
              overflow: "hidden", 
              borderRight: { sm: "none", lg: "1px solid #e2e8f0" }, 
              borderBottom: { xs: "1px solid #e2e8f0", sm: "1px solid #e2e8f0", lg: "none" },
              textDecoration: "none",
              cursor: "pointer",
              transition: "background-color 0.2s",
              "&:hover": { backgroundColor: "#f8fafc" }
            }}
          >
            <Box sx={{ position: "relative", zIndex: 2 }}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
                Total Delivered Orders
              </Typography>
              <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mt: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
                  {stats.totalDeliveredOrders.toLocaleString()}
                </Typography>
                <svg width="48" height="18" viewBox="0 0 48 18" fill="none">
                  <path d="M0 15C8 12 12 2 20 6C28 10 32 4 48 1" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Box>
            </Box>
            <CheckCircleOutlineIcon sx={{ position: "absolute", bottom: -12, right: -12, fontSize: 80, color: "#059669", opacity: 0.05, pointerEvents: "none", zIndex: 1 }} />
          </Box>

          {/* Cell 3: Total Revenue */}
          <Box 
            component={Link}
            to="/analytics"
            sx={{ 
              p: 3, 
              position: "relative", 
              overflow: "hidden", 
              borderRight: { lg: "1px solid #e2e8f0" }, 
              borderBottom: { xs: "1px solid #e2e8f0", sm: "none" },
              textDecoration: "none",
              cursor: "pointer",
              transition: "background-color 0.2s",
              "&:hover": { backgroundColor: "#f8fafc" }
            }}
          >
            <Box sx={{ position: "relative", zIndex: 2 }}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
                Total Revenue
              </Typography>
              <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mt: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
                  ₹{stats.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </Typography>
                <svg width="48" height="18" viewBox="0 0 48 18" fill="none">
                  <path d="M0 16C8 14 12 2 20 4C28 6 32 12 48 1" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Box>
            </Box>
            <PaidOutlinedIcon sx={{ position: "absolute", bottom: -12, right: -12, fontSize: 80, color: "#059669", opacity: 0.05, pointerEvents: "none", zIndex: 1 }} />
          </Box>

          {/* Cell 4: Last Month Revenue */}
          <Box 
            component={Link}
            to="/analytics"
            sx={{ 
              p: 3, 
              position: "relative", 
              overflow: "hidden",
              textDecoration: "none",
              cursor: "pointer",
              transition: "background-color 0.2s",
              "&:hover": { backgroundColor: "#f8fafc" }
            }}
          >
            <Box sx={{ position: "relative", zIndex: 2 }}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
                Last Month Revenue
              </Typography>
              <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mt: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
                  ₹{stats.totalRevenueLastMonth.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </Typography>
                <svg width="48" height="18" viewBox="0 0 48 18" fill="none">
                  <path d="M0 10C8 10 12 16 20 12C28 8 32 2 48 4" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Box>
            </Box>
            <TrendingUpIcon sx={{ position: "absolute", bottom: -12, right: -12, fontSize: 80, color: "#0f172a", opacity: 0.05, pointerEvents: "none", zIndex: 1 }} />
          </Box>
        </Box>

        {/* Operational Alerts Drawer */}
        <Drawer
          anchor="right"
          open={alertsDrawerOpen}
          onClose={() => setAlertsDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: { xs: "100%", sm: 400 },
              p: 3,
              backgroundColor: "#fafafa"
            }
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h6" fontWeight="700" color="#991b1b" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WarningAmberIcon sx={{ color: "#dc2626" }} /> Operational Alerts
            </Typography>
            <IconButton onClick={() => setAlertsDrawerOpen(false)}>
              <ExpandLessIcon sx={{ transform: "rotate(90deg)" }} />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <Stack spacing={3}>
            {stats.alerts?.delayedOrders && stats.alerts.delayedOrders.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight="700" color="#991b1b" sx={{ mb: 1 }}>
                  High Delay Warnings ({stats.alerts.delayedOrders.length})
                </Typography>
                {stats.alerts.delayedOrders.map(order => (
                  <Alert 
                    key={order._id}
                    severity="error" 
                    variant="outlined"
                    sx={{ 
                      borderRadius: 3, 
                      backgroundColor: "#fef2f2", 
                      borderColor: "#fca5a5",
                      mb: 1.5,
                      "& .MuiAlert-message": { width: "100%", display: "flex", flexDirection: "column", gap: 1 }
                    }}
                    action={
                      <Button 
                        size="small" 
                        color="error" 
                        component={Link} 
                        to={`/instant-orders`}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        Track
                      </Button>
                    }
                  >
                    <Typography variant="body2" color="#7f1d1d">
                      Order <strong>#{order.orderNumber}</strong> ({order.customerName}) from <strong>{order.kitchenName}</strong> is delayed by <strong>{order.delayMins} mins</strong>.
                    </Typography>
                  </Alert>
                ))}
              </Box>
            )}

            {stats.alerts?.unassignedOrders && stats.alerts.unassignedOrders.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight="700" color="#854d0e" sx={{ mb: 1 }}>
                  Rider Unassigned Warnings ({stats.alerts.unassignedOrders.length})
                </Typography>
                {stats.alerts.unassignedOrders.map(order => (
                  <Alert 
                    key={order._id}
                    severity="warning" 
                    variant="outlined"
                    sx={{ 
                      borderRadius: 3, 
                      backgroundColor: "#fffbeb", 
                      borderColor: "#fde047",
                      mb: 1.5,
                      "& .MuiAlert-message": { width: "100%", display: "flex", flexDirection: "column", gap: 1 }
                    }}
                    action={
                      <Button 
                        size="small" 
                        color="warning" 
                        component={Link} 
                        to={`/instant-orders`}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        Assign
                      </Button>
                    }
                  >
                    <Typography variant="body2" color="#713f12">
                      Order <strong>#{order.orderNumber}</strong> ({order.customerName}) has been unassigned for <strong>{order.elapsedMins} mins</strong>.
                    </Typography>
                  </Alert>
                ))}
              </Box>
            )}

            {stats.alerts?.lowInventoryStock && stats.alerts.lowInventoryStock.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight="700" color="#0369a1" sx={{ mb: 1 }}>
                  Raw Stock Warnings ({stats.alerts.lowInventoryStock.length})
                </Typography>
                {stats.alerts.lowInventoryStock.map(stock => (
                  <Alert 
                    key={stock._id}
                    severity="info" 
                    variant="outlined"
                    sx={{ 
                      borderRadius: 3, 
                      backgroundColor: "#f0f9ff", 
                      borderColor: "#7dd3fc",
                      mb: 1.5,
                      "& .MuiAlert-message": { width: "100%", display: "flex", flexDirection: "column", gap: 1 }
                    }}
                    action={
                      <Button 
                        size="small" 
                        color="info" 
                        component={Link} 
                        to={`/stocks`}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        Restock
                      </Button>
                    }
                  >
                    <Typography variant="body2" color="#0c4a6e">
                      Item <strong>{stock.itemName}</strong> in kitchen <strong>{stock.kitchenName}</strong> is low: <strong>{stock.quantity} {stock.unit}</strong> remaining.
                    </Typography>
                  </Alert>
                ))}
              </Box>
            )}

            {totalAlertCount === 0 && (
              <Box sx={{ textAlign: "center", mt: 8 }}>
                <Typography variant="body1" color="text.secondary" fontWeight={500}>
                  No active operational alerts.
                </Typography>
              </Box>
            )}
          </Stack>
        </Drawer>

        {/* Split Metrics: Subscription vs. Instant */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 4, mb: 4 }}>
          {/* Subscription Health Column */}
          <Box 
            sx={{ 
              borderRadius: 4, 
              backgroundColor: "white", 
              border: "1px solid #e2e8f0",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              display: "flex",
              flexDirection: "column"
            }}
          >
            {/* Header banner */}
            <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5, borderBottom: "1px solid #e2e8f0", backgroundColor: "#f8fafc" }}>
              <Box sx={{ width: 6, height: 18, borderRadius: 1, backgroundColor: "#10b981" }} />
              <Typography variant="subtitle2" fontWeight="700" color="#0f172a">
                Subscription Health
              </Typography>
            </Box>

            {/* Metrics Grid */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", borderBottom: "1px solid #e2e8f0" }}>
              {/* Cell 1: Active Subscriptions */}
              <Box 
                component={Link}
                to="/subscriptions?status=active"
                sx={{ 
                  p: 3, 
                  borderRight: "1px solid #e2e8f0", 
                  textDecoration: "none", 
                  cursor: "pointer", 
                  transition: "background-color 0.2s", 
                  "&:hover": { backgroundColor: "#f8fafc" } 
                }}
              >
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
                  Active Subscriptions
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" }}>
                    {stats.activeSubscriptions}
                  </Typography>
                  <Box sx={{ p: 1, borderRadius: 2, backgroundColor: "#f0fdf4", color: "#16a34a", display: "flex" }}>
                    <SubscriptionsIcon sx={{ fontSize: 18 }} />
                  </Box>
                </Box>
              </Box>
              {/* Cell 2: Upcoming Morning Deliveries */}
              <Box 
                component={Link}
                to="/orders?status=pending"
                sx={{ 
                  p: 3, 
                  borderRight: "1px solid #e2e8f0", 
                  textDecoration: "none", 
                  cursor: "pointer", 
                  transition: "background-color 0.2s", 
                  "&:hover": { backgroundColor: "#f8fafc" } 
                }}
              >
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
                  Morning Deliveries
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" }}>
                    {stats.upcomingMorningDeliveries || 0}
                  </Typography>
                  <Box sx={{ p: 1, borderRadius: 2, backgroundColor: "#f0fdf4", color: "#16a34a", display: "flex" }}>
                    <ShoppingBasketIcon sx={{ fontSize: 18 }} />
                  </Box>
                </Box>
              </Box>
              {/* Cell 3: New Subscriptions */}
              <Box 
                component={Link}
                to="/subscriptions"
                sx={{ 
                  p: 3, 
                  borderRight: "1px solid #e2e8f0", 
                  textDecoration: "none", 
                  cursor: "pointer", 
                  transition: "background-color 0.2s", 
                  "&:hover": { backgroundColor: "#f8fafc" } 
                }}
              >
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
                  New Subs (7D)
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" }}>
                    {stats.newSubscriptionsPast7Days || 0}
                  </Typography>
                  <Box sx={{ p: 1, borderRadius: 2, backgroundColor: "#f0fdf4", color: "#16a34a", display: "flex" }}>
                    <CalendarMonthIcon sx={{ fontSize: 18 }} />
                  </Box>
                </Box>
              </Box>
              {/* Cell 4: Completed Subscriptions */}
              <Box 
                component={Link}
                to="/subscriptions?status=completed"
                sx={{ 
                  p: 3, 
                  textDecoration: "none", 
                  cursor: "pointer", 
                  transition: "background-color 0.2s", 
                  "&:hover": { backgroundColor: "#f8fafc" } 
                }}
              >
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
                  Completed Subs
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" }}>
                    {stats.completedSubscriptions || 0}
                  </Typography>
                  <Box sx={{ p: 1, borderRadius: 2, backgroundColor: "#eff6ff", color: "#2563eb", display: "flex" }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Bottom Row of Grid */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", flexGrow: 1 }}>
              {/* Cell 4: MTD Revenue */}
              <Box 
                component={Link}
                to="/analytics"
                sx={{ 
                  p: 3, 
                  borderRight: "1px solid #e2e8f0", 
                  display: "flex", 
                  flexDirection: "column", 
                  justifyContent: "center",
                  textDecoration: "none", 
                  cursor: "pointer", 
                  transition: "background-color 0.2s", 
                  "&:hover": { backgroundColor: "#f8fafc" } 
                }}
              >
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
                  Subscription Revenue (MTD)
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#059669", mt: 1, letterSpacing: "-0.01em" }}>
                  ₹{(stats.subscriptionRevenueMTD || 0).toLocaleString()}
                </Typography>
              </Box>
              {/* Cell 5: Total Subscription Revenue */}
              <Box 
                component={Link}
                to="/analytics"
                sx={{ 
                  p: 3, 
                  display: "flex", 
                  flexDirection: "column", 
                  justifyContent: "center",
                  textDecoration: "none", 
                  cursor: "pointer", 
                  transition: "background-color 0.2s", 
                  "&:hover": { backgroundColor: "#f8fafc" } 
                }}
              >
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
                  Total Subscription Revenue
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#059669", mt: 1, letterSpacing: "-0.01em" }}>
                  ₹{(stats.totalSubscriptionRevenue || 0).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Instant Delivery Live Monitor Column */}
          <Box 
            sx={{ 
              borderRadius: 4, 
              backgroundColor: "white", 
              border: "1px solid #e2e8f0",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              display: "flex",
              flexDirection: "column"
            }}
          >
            {/* Header banner */}
            <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5, borderBottom: "1px solid #e2e8f0", backgroundColor: "#f8fafc" }}>
              <Box sx={{ width: 6, height: 18, borderRadius: 1, backgroundColor: "#ea580c" }} />
              <Typography variant="subtitle2" fontWeight="700" color="#0f172a">
                Instant Delivery Live Monitor
              </Typography>
            </Box>

            {/* Metrics Grid */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", borderBottom: "1px solid #e2e8f0" }}>
              {/* Cell 1: Active Live Orders */}
              <Box 
                component={Link}
                to="/instant-orders"
                sx={{ 
                  p: 3, 
                  borderRight: "1px solid #e2e8f0", 
                  textDecoration: "none", 
                  cursor: "pointer", 
                  transition: "background-color 0.2s", 
                  "&:hover": { backgroundColor: "#f8fafc" } 
                }}
              >
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
                  Active Live Orders
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" }}>
                      {stats.activeLiveOrders || 0}
                    </Typography>
                    <Box 
                      sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: "50%", 
                        backgroundColor: "#ea580c", 
                        animation: "pulse-live 2s infinite ease-in-out" 
                      }} 
                    />
                  </Box>
                  <Box sx={{ p: 1, borderRadius: 2, backgroundColor: "#fff7ed", color: "#ea580c", display: "flex" }}>
                    <BoltIcon sx={{ fontSize: 18 }} />
                  </Box>
                </Box>
              </Box>
              {/* Cell 2: Orders Today */}
              <Box 
                component={Link}
                to="/instant-orders/history"
                sx={{ 
                  p: 3, 
                  borderRight: "1px solid #e2e8f0", 
                  textDecoration: "none", 
                  cursor: "pointer", 
                  transition: "background-color 0.2s", 
                  "&:hover": { backgroundColor: "#f8fafc" } 
                }}
              >
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
                  Orders Today
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" }}>
                    {stats.instantOrdersToday || 0}
                  </Typography>
                  <Box sx={{ p: 1, borderRadius: 2, backgroundColor: "#fff7ed", color: "#ea580c", display: "flex" }}>
                    <ShoppingBasketIcon sx={{ fontSize: 18 }} />
                  </Box>
                </Box>
              </Box>
              {/* Cell 3: Avg Orders (Past 7 Days) */}
              <Box 
                component={Link}
                to="/analytics"
                sx={{ 
                  p: 3, 
                  textDecoration: "none", 
                  cursor: "pointer", 
                  transition: "background-color 0.2s", 
                  "&:hover": { backgroundColor: "#f8fafc" } 
                }}
              >
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
                  Avg Orders (7D)
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" }}>
                    {stats.avgInstantOrdersPast7Days || 0}
                  </Typography>
                  <Box sx={{ p: 1, borderRadius: 2, backgroundColor: "#fff7ed", color: "#ea580c", display: "flex" }}>
                    <TrendingUpIcon sx={{ fontSize: 18 }} />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Bottom Row of Grid */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", flexGrow: 1 }}>
              {/* Cell 4: Revenue Today */}
              <Box 
                component={Link}
                to="/analytics"
                sx={{ 
                  p: 3, 
                  borderRight: "1px solid #e2e8f0", 
                  display: "flex", 
                  flexDirection: "column", 
                  justifyContent: "center",
                  textDecoration: "none", 
                  cursor: "pointer", 
                  transition: "background-color 0.2s", 
                  "&:hover": { backgroundColor: "#f8fafc" } 
                }}
              >
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
                  Revenue Today
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#c2410c", mt: 1, letterSpacing: "-0.01em" }}>
                  ₹{(stats.instantRevenueToday || 0).toLocaleString()}
                </Typography>
              </Box>
              {/* Cell 5: Active Kitchens Status */}
              <Box 
                component={Link}
                to="/kitchens"
                sx={{ 
                  p: 3, 
                  borderRight: "1px solid #e2e8f0", 
                  display: "flex", 
                  flexDirection: "column", 
                  justifyContent: "center",
                  textDecoration: "none", 
                  cursor: "pointer", 
                  transition: "background-color 0.2s", 
                  "&:hover": { backgroundColor: "#f8fafc" } 
                }}
              >
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
                  Active Kitchens
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", mt: 1, letterSpacing: "-0.01em" }}>
                    {stats.activeKitchensStr || "0/0"}
                  </Typography>
                  <Box 
                    sx={{ 
                      width: 6, 
                      height: 6, 
                      borderRadius: "50%", 
                      backgroundColor: "#10b981", 
                      animation: "pulse-live 2s infinite ease-in-out",
                      mt: 1
                    }} 
                  />
                </Box>
              </Box>
              {/* Cell 6: Total Instant Revenue */}
              <Box 
                component={Link}
                to="/analytics"
                sx={{ 
                  p: 3, 
                  display: "flex", 
                  flexDirection: "column", 
                  justifyContent: "center",
                  textDecoration: "none", 
                  cursor: "pointer", 
                  transition: "background-color 0.2s", 
                  "&:hover": { backgroundColor: "#f8fafc" } 
                }}
              >
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem" }}>
                  Total Instant Revenue
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#c2410c", mt: 1, letterSpacing: "-0.01em" }}>
                  ₹{(stats.totalInstantRevenue || 0).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        {/* Global Settings / Rain notice */}
        <Box sx={{ mb: 4 }}>
          <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography variant="h6" fontWeight="600" color="#1e293b">
                  System Settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Toggle platform operations globally
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography variant="body1" fontWeight="600" color="#0f172a">
                  Active Rain Notice
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Notifies users of delivery delays and changes checkout policy
                </Typography>
              </Box>
              <Switch
                checked={rainNotice}
                onChange={handleRainNoticeChange}
                color="warning"
              />
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleMorningNotifications}
                sx={{
                  backgroundColor: "#0f172a",
                  color: "white",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "#1e293b",
                  },
                }}
              >
                Send Morning Notifications
              </Button>
              <Button
                variant="contained"
                onClick={handleCustomNotification}
                sx={{
                  backgroundColor: "#0f172a",
                  color: "white",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "#1e293b",
                  },
                }}
              >
                Send Custom Notification
              </Button>
              <Button
                variant="contained"
                onClick={() => setIsInstantSettingsOpen(true)}
                sx={{
                  backgroundColor: "#f97316",
                  color: "white",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "#ea580c",
                  },
                }}
              >
                Configure Instant Orders
              </Button>
            </Box>
          </Card>
        </Box>

        {/* Live Instant Orders Section - Full Width */}
        <Box sx={{ mb: 4 }}>
          <Card elevation={0} sx={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderRadius: 3 }}>
            <CardHeader 
              title={
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                  <Typography variant="h6" fontWeight="600" color="#1e293b" sx={{ fontSize: "1.1rem" }}>
                    Instant Orders
                  </Typography>
                  <Box 
                    sx={{ 
                      px: 1.5, 
                      py: 0.5, 
                      borderRadius: "20px", 
                      backgroundColor: "#d1fae5", 
                      border: "1px solid #a7f3d0",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <Typography variant="caption" fontWeight="700" color="#065f46">
                      {stats.instantOrdersToday || 0} today • ₹{(stats.instantRevenueToday || 0).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              }
              action={
                <Button
                  component={Link}
                  to="/instant-orders"
                  variant="text"
                  size="small"
                  sx={{ textTransform: "none", fontWeight: 600, color: "#ea580c" }}
                >
                  View Live Queue
                </Button>
              }
              sx={{ pb: 1 }}
            />
            <Divider sx={{ borderColor: "#e2e8f0" }} />
            <CardContent sx={{ p: 0 }}>
              {stats.liveInstantOrders && stats.liveInstantOrders.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Elapsed</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.liveInstantOrders.map((order) => (
                        <TableRow key={order._id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>
                            <Link to={`/instant-orders?search=${order.orderNumber}`} style={{ textDecoration: "none", color: "#f97316" }}>
                              #{order.orderNumber}
                            </Link>
                          </TableCell>
                          <TableCell sx={{ color: "#1e293b", fontWeight: 500 }}>
                            {order.itemSummary}
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                  display: "inline-block",
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: "12px",
                                  fontSize: "0.75rem",
                                  fontWeight: 700,
                                  backgroundColor: 
                                    order.status === "Delayed" 
                                      ? "#ffeeef" 
                                      : order.status === "Out for delivery"
                                      ? "#eff6ff"
                                      : order.status === "Prepping"
                                      ? "#fffbeb"
                                      : "#f0fdf4",
                                  color: 
                                    order.status === "Delayed" 
                                      ? "#ef4444" 
                                      : order.status === "Out for delivery"
                                      ? "#2563eb"
                                      : order.status === "Prepping"
                                      ? "#d97706"
                                      : "#16a34a",
                                  border: "1px solid",
                                  borderColor: 
                                    order.status === "Delayed" 
                                      ? "#fecaca" 
                                      : order.status === "Out for delivery"
                                      ? "#bfdbfe"
                                      : order.status === "Prepping"
                                      ? "#fef08a"
                                      : "#bbf7d0",
                              }}
                            >
                              {order.status}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ 
                            color: order.status === "Delayed" ? "#ef4444" : "#64748b",
                            fontWeight: order.status === "Delayed" ? 700 : 500
                          }}>
                            {order.elapsedMins} min
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No active instant orders at the moment
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Collapsible Reference Tables - Equal 3-Column Grid */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr", lg: "1fr 1fr 1fr" }, gap: 3, mb: 4 }}>
          {/* Card 1: Pending Orders */}
          <Card elevation={0} sx={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderRadius: 3, alignSelf: "start" }}>
            <CardHeader 
              title="Next 10 Pending Orders"
              titleTypographyProps={{
                variant: "subtitle2",
                fontWeight: 700,
                sx: { fontSize: "0.85rem", color: "#0f172a" }
              }}
              action={
                <IconButton onClick={() => setPendingOrdersExpanded(!pendingOrdersExpanded)} size="small">
                  {pendingOrdersExpanded ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
                </IconButton>
              }
              sx={{ pb: 1 }}
            />
            {pendingOrdersExpanded && <Divider sx={{ borderColor: "#e2e8f0" }} />}
            {pendingOrdersExpanded && (
              <CardContent sx={{ p: 0 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", color: "#64748b", py: 1 }}>Order ID</TableCell>
                        <TableCell sx={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", color: "#64748b", py: 1 }}>Customer</TableCell>
                        <TableCell sx={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", color: "#64748b", py: 1 }}>Date</TableCell>
                        <TableCell sx={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", color: "#64748b", py: 1 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.next10PendingOrders.map((order) => (
                        <TableRow key={order?._id} hover>
                          <TableCell sx={{ fontSize: "0.8rem" }}>
                            <Link to={`/orders?search=${order?._id}`} style={{ textDecoration: "none", color: "#f97316" }}>
                              {order?._id}
                            </Link>
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.8rem" }}>
                            <Link to={`/users?search=${order?.userId?.name}`} style={{ textDecoration: "none", color: "#64748b" }}>
                              {order?.userId?.name}
                            </Link>
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.8rem" }}>
                            {new Date(order.deliveryDate).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short"
                            })}
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "inline-block",
                                px: 1,
                                py: 0.2,
                                borderRadius: 1,
                                backgroundColor: "#fef3c7",
                                color: "#92400e",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                              }}
                            >
                              {order.status}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            )}
          </Card>

          {/* Card 2: Ending Subscriptions */}
          <Card elevation={0} sx={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderRadius: 3, alignSelf: "start" }}>
            <CardHeader
              title="Subscriptions Ending in 7 Days"
              titleTypographyProps={{
                variant: "subtitle2",
                fontWeight: 700,
                sx: { fontSize: "0.85rem", color: "#0f172a" }
              }}
              action={
                <IconButton onClick={() => setEndingSubscriptionsExpanded(!endingSubscriptionsExpanded)} size="small">
                  {endingSubscriptionsExpanded ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
                </IconButton>
              }
              sx={{ pb: 1 }}
            />
            {endingSubscriptionsExpanded && <Divider sx={{ borderColor: "#e2e8f0" }} />}
            {endingSubscriptionsExpanded && (
              <CardContent sx={{ p: 0 }}>
                {stats.activeSubscriptionsEndingInNext7Days.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", color: "#64748b", py: 1 }}>Subscription ID</TableCell>
                          <TableCell sx={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", color: "#64748b", py: 1 }}>Customer</TableCell>
                          <TableCell sx={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", color: "#64748b", py: 1 }}>End Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.activeSubscriptionsEndingInNext7Days.map((sub) => (
                          <TableRow key={sub?._id} hover>
                            <TableCell sx={{ fontSize: "0.8rem" }}>
                              <Link to={`/subscriptions?search=${sub?._id}`} style={{ textDecoration: "none", color: "#f97316" }}>
                                {sub?._id}
                              </Link>
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8rem" }}>
                              <Link to={`/users?search=${sub?.userId?.name}`} style={{ textDecoration: "none", color: "#64748b" }}>
                                {sub?.userId?.name}
                              </Link>
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.8rem" }}>
                              {new Date(sub?.endDate).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short"
                              })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No subscriptions ending soon
                    </Typography>
                  </Box>
                )}
              </CardContent>
            )}
          </Card>

          {/* Card 3: Support Flags */}
          <Card elevation={0} sx={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderRadius: 3, alignSelf: "start" }}>
            <CardHeader 
              title="Top Support Flags (14 days)"
              titleTypographyProps={{
                variant: "subtitle2",
                fontWeight: 700,
                sx: { fontSize: "0.85rem", color: "#0f172a" }
              }}
              action={
                <IconButton onClick={() => setSupportFlagsExpanded(!supportFlagsExpanded)} size="small">
                  {supportFlagsExpanded ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
                </IconButton>
              }
              sx={{ pb: 1 }}
            />
            {supportFlagsExpanded && <Divider sx={{ borderColor: "#e2e8f0" }} />}
            {supportFlagsExpanded && (
              <CardContent sx={{ p: 0 }}>
                {stats.supportFlags && stats.supportFlags.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", color: "#64748b", py: 1 }}>Customer</TableCell>
                          <TableCell align="right" sx={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", color: "#64748b", py: 1 }}>Cancellations</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.supportFlags.map((u) => (
                          <TableRow key={u._id} hover>
                            <TableCell sx={{ fontSize: "0.8rem" }}>
                              <Link to={`/users?search=${u.user?.name || u._id}`} style={{ textDecoration: "none", color: "#f97316" }}>
                                {u.user?.name || u._id}
                              </Link>
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#ef4444" }}>
                              {u.cancellations}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">No flags registered</Typography>
                  </Box>
                )}
              </CardContent>
            )}
          </Card>
        </Box>

        {/* Quick Redirects Section */}
        <Typography variant="h6" fontWeight="600" sx={{ mb: 2, mt: 4, color: "#1e293b" }}>
          Quick Actions & Operations
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 3, mb: 4 }}>
          {[
            {
              title: "Live Orders",
              desc: "Monitor live incoming instant orders queue",
              path: "/instant-orders",
              icon: <BoltIcon sx={{ color: "#ea580c", fontSize: 28 }} />,
              borderColor: "#ffedd5",
              bg: "#fff7ed",
            },
            {
              title: "Scheduled Orders (Subscription)",
              desc: "Daily morning subscription delivery queue",
              path: "/orders",
              icon: <SubscriptionsIcon sx={{ color: "#059669", fontSize: 28 }} />,
              borderColor: "#d1fae5",
              bg: "#f0fdf4",
            },
            {
              title: "Kitchens",
              desc: "Kitchen statuses & operating hours",
              path: "/kitchens",
              icon: <StorefrontIcon sx={{ color: "#0f172a", fontSize: 28 }} />,
              borderColor: "#e2e8f0",
              bg: "#f8fafc",
            },
            {
              title: "Service Areas Map",
              desc: "Visualize delivery polygons & coverage",
              path: "/service-areas-map",
              icon: <MapIcon sx={{ color: "#4f46e5", fontSize: 28 }} />,
              borderColor: "#e0e7ff",
              bg: "#eef2ff",
            },
            {
              title: "Rider Requests",
              desc: "Approve delivery agent registrations",
              path: "/delivery-agents/requests",
              icon: <BikeScooterIcon sx={{ color: "#06b6d4", fontSize: 28 }} />,
              borderColor: "#cffafe",
              bg: "#ecfeff",
            },
            {
              title: "User Management",
              desc: "View customer profiles, wallets & invoices",
              path: "/users",
              icon: <SupervisorAccountIcon sx={{ color: "#6366f1", fontSize: 28 }} />,
              borderColor: "#e0e7ff",
              bg: "#eef2ff",
            },
            {
              title: "Holidays",
              desc: "Schedule system-wide holiday closures",
              path: "/holidays",
              icon: <LocalOfferIcon sx={{ color: "#ec4899", fontSize: 28 }} />,
              borderColor: "#fce7f3",
              bg: "#fdf2f8",
            },
          ].map((action, i) => (
            <Card 
              key={i}
              component={Link}
              to={action.path}
              elevation={0}
              sx={{
                p: 3,
                textDecoration: "none",
                borderRadius: 4,
                border: "1px solid #e2e8f0",
                backgroundColor: "white",
                position: "relative",
                minHeight: 120,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                cursor: "pointer",
                overflow: "hidden",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 12px 20px -8px rgba(0, 0, 0, 0.08)",
                  borderColor: "#cbd5e1",
                  "& .arrow-icon": {
                    transform: "translateX(4px)",
                    color: "#0f172a",
                  },
                  "& .bg-icon": {
                    opacity: 0.25,
                    transform: "scale(1.1) rotate(-5deg)",
                  }
                }
              }}
            >
              <Box sx={{ pr: 6 }}>
                <Typography 
                  variant="subtitle2" 
                  fontWeight="700" 
                  color="#1e293b" 
                  sx={{ fontSize: "0.95rem", lineHeight: 1.3, letterSpacing: "-0.01em" }}
                >
                  {action.title}
                </Typography>
                <Typography 
                  variant="caption" 
                  color="#64748b" 
                  sx={{ display: "block", mt: 1, lineHeight: 1.4, fontSize: "0.75rem" }}
                >
                  {action.desc}
                </Typography>
              </Box>

              {/* Arrow indicator top-right */}
              <ArrowForwardIcon 
                className="arrow-icon"
                sx={{ 
                  position: "absolute", 
                  top: 24, 
                  right: 24, 
                  fontSize: 18, 
                  color: "#94a3b8",
                  transition: "all 0.2s ease-in-out"
                }} 
              />

              {/* Muted outline icon bottom-right */}
              <Box 
                className="bg-icon"
                sx={{ 
                  position: "absolute", 
                  bottom: -10, 
                  right: -10, 
                  opacity: 0.15,
                  transition: "all 0.3s ease-in-out",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transformOrigin: "bottom right",
                  "& svg": {
                    fontSize: 72,
                  }
                }}
              >
                {action.icon}
              </Box>
            </Card>
          ))}
        </Box>

        <CustomNotificationDialog
          open={openCustomNotificationDialog}
          onClose={() => setOpenCustomNotificationDialog(false)}
        />
        <ConfirmationDialog
          open={openConfirmationDialog}
          onClose={() => setOpenConfirmationDialog(false)}
          onConfirm={async () => {
            setOpenConfirmationDialog(false);
            notificationsApi.triggerMorningNotifications()
              .then(response => {
                console.log("Morning notifications sent successfully:", response);
              })
              .catch(error => {
                console.error("Error sending morning notifications:", error);
              });
          }}
          title="Morning Notifications"
          description="Are you sure you want to send morning notifications?"
        />
        <InstantSettingsDialog
          open={isInstantSettingsOpen}
          onClose={() => setIsInstantSettingsOpen(false)}
          config={instantOrderConfig}
          onSave={handleSaveInstantSettings}
        />
      </>
    );
  };

  return (
    <Layout>
      {renderDashboardContent()}
    </Layout>
  );
};


export default Dashboard;
